/**
 * /frontend/src/features/analytics/pages/OfferAnalyticsPage.js
 */

import React, { useEffect, useMemo, useState } from 'react';
import useOfferAnalytics from '../hooks/useOfferAnalytics';
import useOfferFunnel from '../hooks/useOfferFunnel';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import OfferSummaryKPIs from '../components/offers/OfferSummaryKPIs';
import OfferFunnelChart from '../components/offers/OfferFunnelChart';
import TopOffersChart from '../components/offers/TopOffersChart';
import AudienceComparisonChart from '../components/offers/AudienceComparisonChart';
import OfferInteractionPieChart from '../components/offers/OfferInteractionPieChart';
import OfferEngagementTrendChart from '../components/offers/OfferEngagementTrendChart';
import OfferPerformanceComparisonChart from '../components/offers/OfferPerformanceComparisonChart';
import {
  toOfferAudienceSeries,
  toOfferEngagementTrendSeries,
  toOfferFunnelSeries,
  toOfferInteractionPieSeries,
  toOfferPerformanceComparisonSeries,
  toTopOffersSeries
} from '../utils/chartDataTransformers';
import './OfferAnalyticsPage.css';

const toOfferOptions = (comparison) => (comparison?.offers || []).map((o) => ({
  value: String(o.offerId),
  label: String(o.title || `Offer ${o.offerId}`)
}));

/**
 * Offer analytics page.
 * @param {{request:Function,storeToken:string,storeUuid:string,startDate:string,endDate:string,refreshSignal?:unknown,onSyncingChange?:Function}} props
 * @returns {JSX.Element}
 */
const OfferAnalyticsPage = ({ request, storeToken, storeUuid, startDate, endDate, refreshSignal, onSyncingChange }) => {
  const [offerId, setOfferId] = useState('');
  const hook = useOfferAnalytics({ request, storeToken, storeUuid, startDate, endDate, refreshSignal });
  const summary = hook.data?.summary || null;
  const comparison = hook.data?.comparison || null;
  const byAudience = hook.data?.byAudience || null;

  const options = useMemo(() => toOfferOptions(comparison), [comparison]);
  const selectedOfferId = offerId || (options[0]?.value || '');
  const funnelHook = useOfferFunnel({
    request,
    storeToken,
    offerId: selectedOfferId,
    startDate,
    endDate,
    refreshSignal
  });
  const funnel = funnelHook.data || null;
  const topOffers = useMemo(() => toTopOffersSeries(comparison), [comparison]);
  const offerPerformance = useMemo(() => toOfferPerformanceComparisonSeries(comparison), [comparison]);
  const audienceSeries = useMemo(() => toOfferAudienceSeries(byAudience), [byAudience]);
  const pieSeries = useMemo(() => toOfferInteractionPieSeries(summary), [summary]);
  const trendSeries = useMemo(() => toOfferEngagementTrendSeries(summary), [summary]);
  const funnelSeries = useMemo(() => toOfferFunnelSeries(funnel), [funnel]);

  const isEmpty = useMemo(() => !summary && !comparison && !byAudience, [byAudience, comparison, summary]);
  const syncing = hook.isLoading || hook.isRefreshing || funnelHook.isLoading || funnelHook.isRefreshing;

  useEffect(() => {
    if (typeof onSyncingChange !== 'function') return () => null;
    onSyncingChange(syncing);
    return () => onSyncingChange(false);
  }, [onSyncingChange, syncing]);

  if (hook.isLoading) return <LoadingSkeleton />;
  if (hook.isError) return <ErrorState onRetry={hook.refetch} />;
  if (isEmpty) return <EmptyState message="No offer analytics data for the selected period." />;

  return (
    <div className="analytics-page-stack">
      <OfferSummaryKPIs summary={summary || {}} />

      <OfferPerformanceComparisonChart data={offerPerformance} />

      <div className="analytics-page-controls">
        <label htmlFor="analytics-offer-select">Offer</label>
        <select
          id="analytics-offer-select"
          value={selectedOfferId}
          onChange={(e) => setOfferId(e.target.value)}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="analytics-page-grid-2">
        <OfferFunnelChart data={funnelSeries} />
        <OfferInteractionPieChart data={pieSeries} />
      </div>

      <div className="analytics-page-grid-2">
        <TopOffersChart data={topOffers} />
        <AudienceComparisonChart data={audienceSeries} />
      </div>

      <OfferEngagementTrendChart data={trendSeries} />
    </div>
  );
};

export default OfferAnalyticsPage;
